import { ref } from 'vue';

/**
 * Janela padrão em que os eventos de scroll são creditados ao código.
 * 700ms cobre com folga a animação nativa de `scrollIntoView({ behavior:
 * 'smooth' })` (~300-500ms).
 */
export const DEFAULT_PROGRAMMATIC_SCROLL_WINDOW = 700;

/**
 * Responde a uma única pergunta: este evento de `scroll` foi o usuário ou
 * fomos nós?
 *
 * Precisa ser uma JANELA e não um booleano. Uma rolagem suave emite dezenas
 * de eventos de `scroll` ao longo da animação; o booleano que existia antes
 * cobria só o primeiro deles, e do segundo em diante a própria animação era
 * lida como "o usuário rolou". O efeito visível era a conversa parar de
 * acompanhar mensagem nova até trocar de chat.
 *
 * O preço da janela é o inverso: uma rolagem manual iniciada dentro dela é
 * creditada ao código. Na prática são os ~700ms logo após abrir a conversa
 * ou enviar mensagem — quando o usuário ainda não pôs a mão na roda.
 *
 * @param {number} [windowMs] - duração da janela em milissegundos
 * @returns {{ markProgrammaticScroll: () => void, isProgrammaticScroll: () => boolean }}
 */
export function useScrollOwnership(
  windowMs = DEFAULT_PROGRAMMATIC_SCROLL_WINDOW
) {
  const programmaticUntil = ref(0);

  /**
   * Abre a janela. Todo ponto que rola programaticamente precisa chamar isto
   * — um ponto novo que esquecer faz aquela rolagem contar como manual.
   */
  const markProgrammaticScroll = () => {
    programmaticUntil.value = Date.now() + windowMs;
  };

  /**
   * @returns {boolean} true enquanto a janela aberta pelo último
   * `markProgrammaticScroll()` não expirou.
   */
  const isProgrammaticScroll = () => Date.now() < programmaticUntil.value;

  return { markProgrammaticScroll, isProgrammaticScroll };
}
